"use client";

import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import {
  type CreatorFeedItem,
  takenCount,
  timeLeft,
  wantedCount,
} from "./utils";

/**
 * The request lines that are computed rather than written: time left, taken
 * and wanted counts. The pure functions in utils.ts decide what to say; this
 * hook says it in the creator's language.
 */
export function useRequestCopy() {
  const t = useCreatorTranslations("requests");
  return {
    timeLeft(expiresAt: Date | null, now: Date): string {
      const left = timeLeft(expiresAt, now);
      switch (left.kind) {
        case "untilFilled":
          return t("timeLeft.untilFilled");
        case "closed":
          return t("timeLeft.closed");
        case "daysHours":
          return t("timeLeft.daysHours", {
            days: left.days,
            hours: left.hours,
          });
        case "days":
          return t("timeLeft.days", { days: left.days });
        case "hoursMinutes":
          return t("timeLeft.hoursMinutes", {
            hours: left.hours,
            minutes: left.minutes,
          });
        case "minutes":
          return t("timeLeft.minutes", { minutes: left.minutes });
        case "seconds":
          return t("timeLeft.seconds", { seconds: left.seconds });
      }
    },
    /** "2 of 5 taken", or null for an open-ended request. */
    taken(item: CreatorFeedItem): string | null {
      const count = takenCount(item);
      return count ? t("count.taken", count) : null;
    },
    /** "8 wanted", or null for an open-ended request. */
    wanted(item: CreatorFeedItem): string | null {
      const count = wantedCount(item);
      return count === null ? null : t("count.wanted", { count });
    },
  };
}
